/**
 * Auditor（稽核員）專用佈局 — 對齊 ZHCB design tokens（v3）
 * 白色側邊欄，寬 220px，Logo Indigo→Violet 漸變
 * 頂欄含案件搜尋框 + 稽核行程 Badge
 *
 * v3 變更：菜單路由更新為新 SPA 路徑（/audit/* / /dispatch 等）
 */
import { useMemo } from 'react';
import { Input, Badge, Button, Tooltip } from 'antd';
import {
  LayoutDashboard,
  Landmark,
  Megaphone,
  Send,
  Scale,
  ShieldCheck,
  ClipboardCheck,
  FlaskConical,
  FileBarChart,
  User,
  CalendarDays,
  Search,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import BaseLayout from '@/components/layout/BaseLayout';
import { getMenuForWorkbench } from '@/components/layout/menu-registry';
import type { BaseLayoutConfig, MenuItem } from '@/components/layout/layout-menu-config';
import { useAuthStore } from '@/lib/auth-context';
import { useRouter } from '@/lib/navigation';
import { apiClient } from '@/lib/api-client';
import { useResponsive } from '@/hooks/useResponsive';
import { color, radius, roleAccent, text } from '@/styles/tokens';

/** 稽核任務（僅徽章計數需要的欄位） */
interface AuditTaskScheduleItem {
  id: number;
  plannedAuditMonth: string | null;
}

/**
 * Auditor 菜單 icon 映射表
 */
const AUDITOR_ICON_MAP: Record<string, React.ReactNode> = {
  '/dashboard': <LayoutDashboard size={16} />,
  '/bulletin': <Megaphone size={16} />,
  '/certified-orgs': <Landmark size={16} />,
  '/dispatch': <Send size={16} />,
  '/audit-tasks': <ClipboardCheck size={16} />,
  '/organic-materials': <FlaskConical size={16} />,
  '/decisions': <Scale size={16} />,
  'audit-execution': <ShieldCheck size={16} />,
  'issues-reports': <FileBarChart size={16} />,
  personal: <User size={16} />,
};

/** 為 Auditor 菜單項注入 icon */
function injectAuditorIcons(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    if (!item || !('key' in item)) return item;
    const icon = AUDITOR_ICON_MAP[item.key as string];
    return icon ? { ...item, icon } : item;
  });
}

/** 頂欄左側插槽：案件搜尋 + 稽核行程入口 */
function AuditorHeaderExtra() {
  const router = useRouter();
  const { isMobile, isPadPortrait } = useResponsive();

  // 拉取當前稽核員的任務清單，用於計算「本月待處理」徽章數
  // 後端對 auditor 角色自動只回自己的任務，無需傳 assigneeId
  const { data } = useQuery<{ items: AuditTaskScheduleItem[] }>({
    queryKey: ['audit-tasks', 'auditor-badge'],
    queryFn: () => apiClient.get('/api/audit-tasks'),
    staleTime: 60_000,
  });

  // 計算本月（YYYY-MM）任務數；count=0 時 antd Badge 自動隱藏
  const currentMonth = dayjs().format('YYYY-MM');
  const monthlyTaskCount =
    data?.items?.filter((t) => t.plannedAuditMonth === currentMonth).length ?? 0;

  return (
    <>
      {/* mobile 上隱藏案件搜尋框（⌘K 已能涵蓋此功能），Pad 直立縮窄 */}
      {!isMobile && (
        <Input
          placeholder="搜尋案件編號或組織名…"
          prefix={<Search size={14} style={{ color: color.text.muted }} />}
          style={{
            width: isPadPortrait ? 180 : 320,
            borderRadius: radius.md,
            background: color.surface.muted,
            fontSize: text.md,
          }}
          allowClear
        />
      )}
      <Tooltip title="稽核行程">
        <Badge count={monthlyTaskCount} size="small" offset={[-2, 4]}>
          <Button
            type="text"
            icon={<CalendarDays size={18} />}
            onClick={() => router.push('/auditor/schedule')}
            style={{ color: color.text.tertiary, width: 36, height: 36 }}
            aria-label="稽核行程"
          />
        </Badge>
      </Tooltip>
    </>
  );
}

interface AuditorLayoutProps {
  children: React.ReactNode;
  focusMode?: boolean;
}

export default function AuditorLayout({ children, focusMode }: AuditorLayoutProps) {
  /**
   * 多角色：user 含 decision.vote 能力時菜單額外顯示「我的審議任務」（一人一賬號雙角色）。
   * Task 9 重構：直接調用 getMenuForWorkbench('auditor', user, user.menuHidden ?? [])，
   * 傳入真實用戶物件 + DB menuHidden 黑名單，取代原 getAuditorMenuItems 封裝。
   */
  const user = useAuthStore((s) => s.user);

  const config: BaseLayoutConfig = useMemo(
    () => ({
      sider: {
        width: 220,
        background: color.surface.base,
        borderRight: `1px solid ${color.border.subtle}`,
      },
      logo: {
        title: '稽核工作臺',
        titleColor: color.text.primary,
        gradient: roleAccent.auditor.gradient,
      },
      menuItems: injectAuditorIcons(
        getMenuForWorkbench(
          'auditor',
          user ? { capabilities: user.capabilities, roles: user.roles } : {},
          user?.menuHidden ?? [],
        ),
      ),
      headerLeftExtra: <AuditorHeaderExtra />,
    }),
    [user],
  );

  return (
    <BaseLayout config={config} focusMode={focusMode}>
      {children}
    </BaseLayout>
  );
}
